import { NetworkStatus } from '@apollo/client';
import {
  AvatarSize,
  Box,
  FormControl,
  MultiSelect,
  Text,
  TextFieldAppearance,
  TextFieldSize,
  ToggleSwitch,
  useModalState,
} from '@bechtel/gds';
import { useAppUser, useName, useSearchToolRoom } from '@bechtel/ht-clients-common';
import { OperatorType } from '@bechtel/ht-types';
import { Field } from 'formik';
import { get } from 'lodash';
import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { ActionContent, ErrorBoundary } from '~/components';
import { ConfirmDialog } from '~/components/confirm-dialog';
import UserPhoto from '~/components/molecules/user-photo';

import { useSearchRoles } from '~/hooks/use-search-role';
import { PrimaryButtonStyled } from '~/styles';
import { getFormatedDate } from '~/utilities';
import { checkToolRoomsRoles } from '../form-state';
import { TextFieldStyled } from './item/styled';
import { ErrorField, FormControlLabel, UserInfo } from './item/user-info';
import { CancelButtonStyled, FooterStyled, SepartorStyled } from './styled';
import { UserInfoDetailProps } from './types';

export const UserInfoDetail: FC<UserInfoDetailProps> = ({
  userDetail,
  mutationLoading,
  formikProps,
  rolePermissionList,
}) => {
  const { setFieldValue, values, dirty, isValid, handleSubmit, errors } = formikProps;
  const { getFullName } = useName();
  const navigate = useNavigate();
  const { appUser } = useAppUser();
  const imageUrl = userDetail?.employee?.imageUrl;
  const userFulllName = userDetail ? getFullName(userDetail.firstName, userDetail.lastName) : `New User`;

  const { searchRoles, data: roleListData, loading: roleLoading, networkStatus: roleNetworkStatus } = useSearchRoles();
  const { searchToolRoom, data: toolRoomListData, loading: toolRoomLoading } = useSearchToolRoom();

  const { open: openConfirmDialog, close: closeConfirmDialog, visible: visibleConfirmDialog } = useModalState(false);
  useEffect(() => {
    searchRoles({
      sortInput: [],
      filterInput: {},
    });
    searchToolRoom({
      sortInput: [],
      filterInput: {
        and: [
          { field: 'projectId', operator: OperatorType.EQUAL, value: appUser?.defaultProjectId },
          { field: 'isActive', operator: OperatorType.EQUAL, value: 'true' },
        ],
      },
    });
  }, []);

  const roleList = get(roleListData, 'searchRoles.items', []);
  const toolRoomList = get(toolRoomListData, 'searchToolRooms.items', []);

  const Item = ({ item }: any) => {
    return (
      <Text size="400" color="grey.1000">
        {item.description}
      </Text>
    );
  };

  const handleCancel = () => {
    if (dirty) {
      openConfirmDialog();
    } else {
      navigate('/project-admin/user-admin');
    }
  };
  return (
    <ErrorBoundary>
      <Box>
        {userDetail && userDetail.id.length > 0 && (
          <Box mr={{ sm: '400' }}>
            <UserPhoto
              backgroundColor="white"
              imageUrl={imageUrl || ''}
              size={AvatarSize.LARGE}
              fontSize="600"
              user={userDetail}
            ></UserPhoto>
          </Box>
        )}
        <Box
          flexDirection={{ sm: 'column' }}
          flexGrow={{ sm: '1' }}
          style={{ pointerEvents: userDetail ? 'auto' : 'none' }}
        >
          <Box mb={{ sm: '450' }} style={{ whiteSpace: 'nowrap' }}>
            <Text family="openSans" color="primary.700" size="500" weight="600">
              {userFulllName}
            </Text>
          </Box>
          <Box gap={{ sm: '500' }}>
            <Box>
              <Box mr={{ sm: '450' }}>
                <ToggleSwitch
                  isToggled={values.isActive ?? false}
                  onToggle={() => {
                    setFieldValue('isActive', !values.isActive);
                  }}
                />
              </Box>
              <Text family="openSans" size="300" color="primary.700">
                {values.isActive ? `Active` : `Inactive`}
              </Text>
            </Box>
            <Box>
              <Box mr={{ sm: '450' }}>
                <ToggleSwitch
                  isToggled={values.exemptedFromImport ?? false}
                  onToggle={() => {
                    setFieldValue('exemptedFromImport', !values.exemptedFromImport);
                  }}
                />
              </Box>
              <Text family="openSans" size="300" color="primary.700">
                Exempted From Import
              </Text>
            </Box>
          </Box>
        </Box>
        {userDetail?.employee?.updatedDate && (
          <Box flexDirection={{ sm: 'column' }} gap={{ sm: '200' }} pr={{ sm: '400' }}>
            <Text family="openSans" color="grey.600" size="300" weight="400">
              Updated On
            </Text>
            <Text family="openSans" color="grey.600" size="300" weight="400">
              {getFormatedDate(userDetail?.employee?.updatedDate)}
            </Text>
          </Box>
        )}
      </Box>
      <SepartorStyled ml={{ sm: '600' }} style={{ opacity: '0.1' }} mt={{ sm: '400' }} />
      <Box
        flexDirection={{ sm: 'column' }}
        pt={{ sm: '500' }}
        pb={{ sm: '700' }}
        flexGrow={{ sm: '1' }}
        style={{ overflowY: 'auto', height: '200px' }}
      >
        <Box flexDirection={{ sm: 'column' }}>
          <Box mb={{ sm: '400' }}>
            <Text size="400" color="primary.700" weight="600">
              User Detail
            </Text>
          </Box>
          <SepartorStyled mb={{ sm: '600' }} />

          <UserInfo formikProps={formikProps} userDetail={userDetail} />

          <Box pt={{ sm: '500' }}>
            <Text size="400" color="grey.1000">
              Supervisor
            </Text>
          </Box>
          <SepartorStyled mt={{ sm: '400' }} mb={{ sm: '550' }} />
          <UserInfo formikProps={formikProps} userDetail={userDetail?.employee?.supervisor} isSupervisorDetail={true} />
          <Box pt={{ sm: '500' }}>
            <Text size="400" color="grey.1000">
              Project Roles
            </Text>
          </Box>
          <SepartorStyled mt={{ sm: '400' }} mb={{ sm: '550' }} />
          <Box mb={{ sm: '500' }} mr={{ sm: '500' }}>
            <FormControl style={{ flexGrow: 1 }} label={<FormControlLabel label={`Toolhound Role`} />}>
              <Field name={'thRole'}>
                {({ field }) => {
                  return <TextFieldStyled disabled={true} {...field} />;
                }}
              </Field>
            </FormControl>
          </Box>
          <Box mr={{ sm: '500' }}>
            <Box flexDirection={{ sm: 'column' }} flexBasis={{ sm: '100%' }}>
              <FormControl style={{ flexGrow: 1 }} label={<FormControlLabel label={`Assign Role`} />}>
                <Field name="roles">
                  {({ field }) => {
                    return (
                      <MultiSelect
                        {...field}
                        size={TextFieldSize.MEDIUM}
                        items={roleList}
                        initialSelectedItems={values.roles}
                        itemComponent={Item}
                        selectedItems={values.roles}
                        displayKeyField="description"
                        appearance={TextFieldAppearance.NORMAL}
                        loading={roleLoading || roleNetworkStatus === NetworkStatus.refetch}
                        onSelectedItemsChange={async (changes) => {
                          const { selectedItems } = changes;
                          const isToolRoomRoles = checkToolRoomsRoles(selectedItems || [], rolePermissionList);
                          if (!isToolRoomRoles) {
                            setFieldValue('toolRooms', []);
                            values.toolRooms = [];
                          }
                          setFieldValue('roles', selectedItems);
                        }}
                      />
                    );
                  }}
                </Field>
              </FormControl>
              {errors.roles ? <ErrorField error={errors.roles} /> : null}
            </Box>
          </Box>
          <Box mr={{ sm: '500' }} pt={{ sm: '500' }} mb={{ sm: '400' }}>
            <Box flexDirection={{ sm: 'column' }} flexBasis={{ sm: '100%' }}>
              <FormControl
                style={{ flexGrow: 1 }}
                label={
                  <FormControlLabel
                    label={`Assign Tool Rooms`}
                    disabled={!checkToolRoomsRoles(values.roles, rolePermissionList)}
                  />
                }
              >
                <Field name="toolRooms">
                  {({ field }) => {
                    return (
                      <MultiSelect
                        autoFocus={false}
                        disabled={!checkToolRoomsRoles(values.roles, rolePermissionList)}
                        {...field}
                        size={TextFieldSize.MEDIUM}
                        items={toolRoomList}
                        initialSelectedItems={values.toolRooms}
                        itemComponent={Item}
                        selectedItems={values.toolRooms}
                        displayKeyField="description"
                        appearance={TextFieldAppearance.NORMAL}
                        loading={toolRoomLoading || roleNetworkStatus === NetworkStatus.refetch}
                        onSelectedItemsChange={async (changes) => {
                          const { selectedItems } = changes;
                          setFieldValue('toolRooms', selectedItems);
                        }}
                      />
                    );
                  }}
                </Field>
              </FormControl>
              {errors.toolRooms ? <ErrorField error={errors.toolRooms} /> : null}
            </Box>
          </Box>
        </Box>
      </Box>
      <FooterStyled>
        <CancelButtonStyled onClick={handleCancel} disabled={false}>
          <Text color="grey.1000" size="300">
            Cancel
          </Text>
        </CancelButtonStyled>

        <PrimaryButtonStyled onClick={() => handleSubmit()} disabled={!isValid || mutationLoading || !dirty}>
          <ActionContent icon="save" iconStyle="solid" label={values.id ? 'Update' : 'Save'} />
        </PrimaryButtonStyled>
      </FooterStyled>
      {visibleConfirmDialog && (
        <ConfirmDialog
          message={'Are you sure you want to discard changes?'}
          onCancel={closeConfirmDialog}
          title="Discard Changes"
          onConfirm={() => {
            navigate('/project-admin/user-admin');
          }}
          cancelText="NO, LEAVE AS IS"
          confirmText="Confirm"
        ></ConfirmDialog>
      )}
    </ErrorBoundary>
  );
};
